import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Popover, Tooltip } from "antd";
import { Locale as AntdLocale } from "antd/lib/locale";
import zhCN from "antd/locale/zh_CN";
import zhTW from "antd/locale/zh_TW";
import enUs from "antd/locale/en_US";
import viVN from "antd/locale/vi_VN";
import koKR from "antd/locale/ko_KR";
import { UserLangEnum, UserSettingEnum } from "@/types/user";
import localeIcon from "images/system/localeIcon.png";
import checkedIcon from "images/common/checked.svg";
import { UserContext } from "@/layout/context/SettingsContext";
import { userUpdateSetting } from "@/apis/user";
import { setStorage } from "@/utils/storage";
import i18n from "@/locale";
import { styled } from "styled-components";

interface LangItem {
  value: string;
  apply: AntdLocale;
  key: UserLangEnum;
  appApply: string;
}

const LangItemWarp = styled.div`
  cursor: pointer;
  line-height: 30px;

  &:hover {
    background-color: #f0f0f0;
  }
`;

export const LANGS: Array<LangItem> = [
  { value: "简体中文", apply: zhCN, key: UserLangEnum.ZH, appApply: "zh" },
  { value: "English", apply: enUs, key: UserLangEnum.EN, appApply: "en" },
  { value: "繁體中文", apply: zhTW, key: UserLangEnum.TR, appApply: "tr" },
  { value: "Tiếng Việt", apply: viVN, key: UserLangEnum.VN, appApply: "vn" },
  { value: "한국인", apply: koKR, key: UserLangEnum.KO, appApply: "ko" },
];

const Locale: FC = () => {
  const { t } = useTranslation();
  const [[curUser], [userSettings, setUserSettings]] = useContext(UserContext);
  const currentLang =
    userSettings.find((setting) => setting.type === UserSettingEnum.LANG)
      ?.value ?? UserLangEnum.EN;

  const handleUpdateLang = (lang: LangItem) => {
    if (curUser) {
      const newUpdate = [
        ...userSettings.filter((item) => item.type !== UserSettingEnum.LANG),
        { type: UserSettingEnum.LANG, value: lang.key },
      ];
      userUpdateSetting(curUser?.id, newUpdate).then(() => {
        setUserSettings(newUpdate);
      }); // set data for backend and component info
      setStorage("locale", lang.key); // next login lang
      i18n.changeLanguage(lang.appApply); // current info lang
    }
  };

  return (
    <Popover
      trigger={["click"]}
      placement="right"
      content={
        <div style={{ width: "120px" }}>
          {LANGS.map((lang) => (
            <LangItemWarp
              key={lang.key}
              onClick={() => handleUpdateLang(lang)}
              style={{
                fontWeight: currentLang === lang.key ? "bold" : "normal",
              }}
            >
              <span className="label">{lang.value}</span>
              {currentLang === lang.key && (
                <span
                  className="checked"
                  style={{ float: "right", padding: "4px" }}
                >
                  <img src={checkedIcon} alt="" />
                </span>
              )}
            </LangItemWarp>
          ))}
        </div>
      }
    >
      <Tooltip title={t("layout.changeTip")}>
        <img
          src={localeIcon}
          alt=""
          width={16}
          height={16}
          style={{ cursor: "pointer" }}
        />
      </Tooltip>
    </Popover>
  );
};

export default Locale;
